import { IconButton, TextInput } from "@ameelio/ui";
import SendIcon from "@mui/icons-material/Send";
import { Box, useMediaQuery as measureScreenWidth } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSound } from "use-sound";
import addStylesForDevices from "../../lib/addStylesForDevices";
import { belowLargeTablet } from "../../lib/responsiveHelpers";
import chatSendSound from "./sounds/chat_send.mp3";

export type Props = {
  label: string;
  onSend?: (message: string) => void;
  muteSounds?: boolean;
};

type FormData = {
  message: string;
};

export default function ChatInput({ label, onSend, muteSounds }: Props) {
  const { t } = useTranslation();

  const [playMessageSent] = useSound(chatSendSound, { volume: 0.5 });
  const { control, watch, handleSubmit, reset } = useForm<FormData>({
    mode: "onTouched",
  });
  const messageContent = watch("message");
  const isMobileOrSmallTablet = belowLargeTablet(measureScreenWidth);

  const onSubmit = (data: FormData) => {
    if (!onSend || !data.message || data.message.trim().length === 0) return;
    onSend(data.message.trim());
    if (!muteSounds) playMessageSent();
    reset();
  };

  const sendDisabled = !onSend || (messageContent || "").trim().length === 0;

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ display: "flex" }}>
      <Box sx={{ display: "flex", flexGrow: 1 }}>
        <TextInput
          control={control}
          name="message"
          label={label}
          autoComplete="off"
          sx={addStylesForDevices(
            isMobileOrSmallTablet,
            { flexGrow: 1 },
            { width: 1 }
          )}
          disabled={!onSend}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          pt: 1,
        }}
      >
        <IconButton
          tooltip
          size="large"
          type="submit"
          ariaLabel={sendDisabled ? t("Sending is disabled") : t("Send")}
          disabled={sendDisabled}
        >
          <SendIcon color={sendDisabled ? "disabled" : "info"} />
        </IconButton>
      </Box>
    </form>
  );
}
